
import {
    SoccerIcon, BasketballIcon, TennisIcon, BaseballIcon,
    AmericanFootballIcon, RugbyIcon, VolleyballIcon,
    IceHockeyIcon, BoxingIcon, MMAIcon, EsportsIcon,
    MotorsportIcon, CricketIcon, TableTennisIcon,
    HandBallIcon, DartsIcon, CyclingIcon, BadmintonIcon, GenericTrophyIcon,
    Waves, Flame,
    BeachVolleyIcon, FutsalIcon, ArcheryIcon, BandyIcon, AlpineSkiingIcon,
    AthleticsIcon, ArtisticSwimmingIcon, AirRacingIcon, AussieRulesIcon
} from './sportIcons';

/**
 * Priorities for sorting sports. Higher value = Higher priority.
 */
export const SPORT_PRIORITIES = {
    'esports': 60
};

export const SPORT_ID_TO_NAME = {
    1: 'Fútbol',
    2: 'Tenis',
    3: 'Baloncesto',
    4: 'Hockey Hielo',
    5: 'Béisbol',
    6: 'Fútbol Americano',
    7: 'Voleibol',
    9: 'Fútbol Australiano',
    10: 'Vóley Playa',
    12: 'Futsal',
    13: 'Rugby',
    14: 'Bádminton',
    16: 'Balonmano',
    17: 'Tenis de Mesa',
    19: 'Criquet',
    25: 'Waterpolo',
    34: 'Esquí Alpino',
    35: 'Bandy',
    74: 'Tiro con Arco',
    75: 'Atletismo',
    84: 'Canicas',
    85: 'Carrera Aérea',
    87: 'Natación',
    89: 'Natación Artística',
    301: 'Arena of Valor'
};

export const getSportName = (sport) => {
    // Prefer processed 'sportId' but also check 'id' and 'sport_id'
    const id = Number(sport?.sportId || sport?.id || sport?.sport_id || sport?.sportId);
    if (SPORT_ID_TO_NAME[id]) return SPORT_ID_TO_NAME[id];
    return translateSportName(sport?.sportName || sport?.name || sport?.sport_name);
};

/**
 * Returns the appropriate Icon component based on the sport name or ID.
 * @param {Object} sport - The sport object containing name and id.
 * @returns {Component} Icon component.
 */
export const getSportIcon = (sport) => {
    // 1. Prioritize processed fields (sportName, sportId)
    const name = (sport?.sportName || sport?.name || sport?.sport_name || "").toLowerCase().trim();
    // 2. Be careful NOT to use event ID (e.g. 54321) as Sport ID
    const id = Number(sport?.sportId || sport?.sport_id || sport?.id);

    // 0. VIP: Mundial 2026 (categoría especial id 1000)
    if (id === 1000 || name.includes('mundial')) return GenericTrophyIcon;

    // 1. OTHERS/SPECIFIC
    if (name.includes('beach vol') || name.includes('vóley playa') || name.includes('playa') || id === 10) return BeachVolleyIcon;
    if (name.includes('futsal') || id === 12) return FutsalIcon;
    if (name.includes('archery') || name.includes('tiro con arco') || id === 74) return ArcheryIcon;
    if (name.includes('bandy') || id === 35) return BandyIcon;
    if (name.includes('alpine') || name.includes('alpino') || name.includes('ski') || id === 34) return AlpineSkiingIcon;
    if (name.includes('athletics') || name.includes('atletismo') || id === 75) return AthleticsIcon;
    if (name.includes('artistic') || name.includes('artística') || id === 89) return ArtisticSwimmingIcon;
    if (name.includes('air racing') || name.includes('aéreas') || id === 85) return AirRacingIcon;
    if (name.includes('aussie') || name.includes('australian') || id === 9) return AussieRulesIcon;
    if (name.includes('canicas') || id === 84) return GenericTrophyIcon;

    // 2. SOCCER (But ensure it's not Am. Football or Aussie Rules)
    if (name.includes('soccer') || name.includes('fútbol') || name.includes('futbol') || name.includes('football') || id === 1) {
        if (name.includes('american') || name.includes('americano') || id === 6) return AmericanFootballIcon;
        if (name.includes('australiano') || id === 9) return AussieRulesIcon;
        // Check if tennis keyword leaked into name unintentionally, though processed sportName should be clean
        if (name.includes('tennis') || name.includes('tenis')) return TennisIcon;
        return SoccerIcon;
    }

    // 3. TENNIS
    if (name.includes('table tennis') || name.includes('mesa') || id === 17) return TableTennisIcon;
    if (name === 'tennis' || name === 'tenis' || name.includes('tennis') || name.includes('tenis') || id === 2) return TennisIcon;

    // 4. BASKETBALL
    if (name.includes('basketball') || name.includes('baloncesto') || name.includes('basket') || id === 3) return BasketballIcon;

    // 5. BASEBALL
    if (name.includes('baseball') || name.includes('béisbol') || name.includes('beisbol') || id === 5) return BaseballIcon;

    // 6. ICE HOCKEY
    if (name.includes('ice hockey') || name.includes('hielo') || name.includes('hockey') || id === 4) return IceHockeyIcon;

    // 7. VOLLEYBALL
    if (name.includes('volleyball') || name.includes('voleibol') || id === 7) return VolleyballIcon;

    // 8. HANDBALL
    if (name.includes('handball') || name.includes('balonmano') || id === 16) return HandBallIcon;

    // 9. RUGBY
    if (name.includes('rugby') || id === 13) return RugbyIcon;

    // 10. CRICKET
    if (name.includes('cricket') || name.includes('criquet') || id === 19) return CricketIcon;

    // 11. FIGHTING
    if (name.includes('mma') || name.includes('ufc')) return MMAIcon;
    if (name.includes('boxing') || name.includes('boxeo')) return BoxingIcon;

    // 12. ESPORTS
    if (name.includes('esport') || name.includes('e-sport') || name.includes('valorant') || name.includes('league') || name.includes('dota') || name.includes('cs:go') || name.includes('arena of valor') || id === 301) return EsportsIcon;

    // 13. MOTORSPORT
    if (name.includes('motor') || name.includes('racing') || name.includes('f1') || name.includes('formula')) return MotorsportIcon;

    // 14. AQUATICS
    if (name.includes('water') || name.includes('polo') || name.includes('aquatic') || name.includes('natación') || id === 25 || id === 87) return Waves;

    // 15. OTHERS
    if (name.includes('darts') || name.includes('dardos')) return DartsIcon;
    if (name.includes('cycling') || name.includes('ciclismo')) return CyclingIcon;
    if (name.includes('badminton') || name.includes('bádminton') || id === 14) return BadmintonIcon;
    if (name.includes('snooker')) return GenericTrophyIcon;
    if (name.includes('golf')) return GenericTrophyIcon;

    return GenericTrophyIcon;
};

export const detectRealSport = (event) => {
    if (!event) return null;

    // Normalize inputs
    const leagueRaw = (event.tournament?.name || event.league || "").toLowerCase().trim();
    const homeRaw = (event.competitors?.[0]?.name || event.team1 || "").toLowerCase().trim();
    const awayRaw = (event.competitors?.[1]?.name || event.team2 || "").toLowerCase().trim();
    const sportNameRaw = (event.sport_name || "").toLowerCase().trim();

    // 1. TENNIS DETECTION (ID 2)
    // Strong signals: "wta", "atp", "itf", "challenger", "singles", "doubles"
    // Name format: "Surname, Firstname" (comma) -> Very strong tennis signal but check not esports
    const hasCommaFormat = (homeRaw.includes(',') && !homeRaw.includes(' fc') && !homeRaw.includes('united')) ||
        (awayRaw.includes(',') && !awayRaw.includes(' fc') && !awayRaw.includes('united'));

    const isTennisLeague =
        leagueRaw.includes('wta') ||
        leagueRaw.includes('atp') ||
        leagueRaw.includes('itf') ||
        leagueRaw.includes('challenger') ||
        leagueRaw.includes('singles') ||
        leagueRaw.includes('doubles') ||
        leagueRaw.includes('roland garros') ||
        leagueRaw.includes('wimebledon') || // Typo fix? wimbledon
        leagueRaw.includes('wimbledon') ||
        leagueRaw.includes('us open') ||
        leagueRaw.includes('australian open') ||
        leagueRaw.includes('davis cup');

    if (isTennisLeague) return 2;
    if (sportNameRaw.includes('tennis') || sportNameRaw.includes('tenis')) return 2;
    if (hasCommaFormat && !leagueRaw.includes('esport') && !leagueRaw.includes('fifa') && !leagueRaw.includes('cyber')) return 2;

    // 2. BASKETBALL DETECTION (ID 3)
    const isBasketLeague =
        leagueRaw.includes('nba') ||
        leagueRaw.includes('euroleague') ||
        leagueRaw.includes('acb') ||
        leagueRaw.includes('basket') ||
        leagueRaw.includes('baloncesto') ||
        leagueRaw.includes('wnba');

    if (isBasketLeague) return 3;
    if (sportNameRaw.includes('basket') || sportNameRaw.includes('baloncesto')) return 3;

    // 3. BASEBALL DETECTION (ID 5)
    // ...
    const isBaseballLeague =
        leagueRaw.includes('mlb') ||
        leagueRaw.includes('baseball') ||
        leagueRaw.includes('béisbol') ||
        leagueRaw.includes('kbo') || // Korean
        leagueRaw.includes('npb'); // Japan

    if (isBaseballLeague) return 5;
    if (sportNameRaw.includes('baseball') || sportNameRaw.includes('béisbol')) return 5;

    // 4. ESPORTS (ID 301 / 18 etc)
    const isEsports =
        leagueRaw.includes('esport') ||
        leagueRaw.includes('cyber') ||
        leagueRaw.includes('fifa') ||
        leagueRaw.includes('electronic') ||
        leagueRaw.includes('valorant') ||
        leagueRaw.includes('battleground') ||
        leagueRaw.includes('league of legends') ||
        sportNameRaw.includes('esport');

    if (isEsports) return 301; // Or custom esports ID

    // 5. SOCCER DETECTION (ID 1)
    if (sportNameRaw.includes('soccer') || sportNameRaw.includes('fútbol') || sportNameRaw.includes('football')) return 1;

    // Default: Return whatever ID came with the event or null
    // We prefer returning the numeric ID if available
    const originalId = Number(event.sport_id || event.sportId || event.sport_key);
    return originalId || null;
};

export const isMatchLive = (event) => {
    if (!event || !event.start_time) return false;
    const now = new Date();
    const start = new Date(event.start_time);
    if (now < start) return false;
    const hoursSinceStart = (now - start) / (1000 * 60 * 60);
    return hoursSinceStart < 5;
};

export const translateSportName = (name) => {
    if (!name) return "";
    const n = name.toString().trim();

    const map = {
        "Soccer": "Fútbol",
        "Basketball": "Baloncesto",
        "Tennis": "Tenis",
        "Ice Hockey": "Hockey Hielo",
        "Table Tennis": "Tenis de Mesa",
        "Volleyball": "Voleibol",
        "Handball": "Balonmano",
        "Baseball": "Béisbol",
        "Waterpolo": "Waterpolo",
        "Beach Volley": "Vóley Playa",
        "Cricket": "Criquet",
        "Futsal": "Futsal",
        "Rugby": "Rugby",
        "American Football": "Fútbol Americano",
        "Boxing": "Boxeo",
        "MMA": "MMA",
        "Darts": "Dardos",
        "Cycling": "Ciclismo",
        "Motorsport": "Motor",
        "Badminton": "Bádminton",
        "Snooker": "Snooker",
        "Golf": "Golf",
        "7BallRun": "Canicas", // If this is marble racing
        "Air Racing": "Carrera Aérea",
        "Alpine Skiing": "Esquí Alpino",
        "Arena of Valor": "Arena of Valor",
        "Aquatics": "Natación",
        "Archery": "Tiro con Arco",
        "Artistic Swimming": "Natación Artística",
        "Athletics": "Atletismo",
        "Aussie Rules": "Fútbol Australiano",
        "Bandy": "Bandy",
        "Biathlon": "Biatlón",
        "Floorball": "Floorball",
        "Formula 1": "Fórmula 1",
        "Ski Jumping": "Salto de Esquí",
        "Skydiving": "Paracaidismo",
        "Speedway": "Speedway",
        "Surfing": "Surf"
    };

    return map[n] || map[n.charAt(0).toUpperCase() + n.slice(1)] || name;
};
